import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { NewsItem } from "@/mocks/news";

export default function HeroCarousel({ items }: { items: NewsItem[] }) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 5500);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  const go = (dir: number) => {
    setActive((prev) => (prev + dir + items.length) % items.length);
  };

  return (
    <section className="relative w-full h-[440px] md:h-[600px] overflow-hidden bg-foreground-950">
      {items.map((item, index) => (
        <Link
          key={item.id}
          to={`/news/detail/${item.id}`}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"
          }`}
        >
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} - 白石茉莉奈焦点资讯`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/55 to-foreground-950/20"></div>
          <div className="absolute inset-0">
            <div className="h-full px-4 md:px-8 flex flex-col justify-center max-w-4xl">
              <span className="inline-flex items-center gap-2 w-fit text-xs font-body px-3 py-1.5 rounded-full bg-accent-500 text-foreground-950 mb-4 md:mb-5">
                <i className="ri-flashlight-fill"></i>
                焦点资讯 · {item.category}
              </span>
              <h2 className="font-heading text-2xl md:text-4xl lg:text-5xl text-background-50 leading-tight max-w-3xl">
                {item.title}
              </h2>
              <p className="text-sm md:text-base text-foreground-200 leading-relaxed mt-4 md:mt-5 max-w-2xl line-clamp-3 font-body">
                {item.summary}
              </p>
              <div className="flex items-center gap-4 mt-5 md:mt-7 text-xs md:text-sm text-foreground-300 font-body">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {item.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {item.author}
                </span>
                <span className="hidden sm:flex items-center gap-1.5">
                  <i className="ri-fire-line"></i>
                  {item.views.toLocaleString()} 阅读
                </span>
              </div>
            </div>
          </div>
        </Link>
      ))}

      <div className="absolute inset-x-0 bottom-5 md:bottom-8 z-20 px-4 md:px-8 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActive(index)}
              aria-label={`查看第 ${index + 1} 条焦点资讯`}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                index === active ? "w-8 bg-accent-500" : "w-3 bg-background-50/40"
              }`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="上一条"
            className="w-9 h-9 md:w-10 md:h-10 rounded-full border border-background-50/40 text-background-50 flex items-center justify-center hover:bg-background-50/15 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            aria-label="下一条"
            className="w-9 h-9 md:w-10 md:h-10 rounded-full border border-background-50/40 text-background-50 flex items-center justify-center hover:bg-background-50/15 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
        </div>
      </div>
    </section>
  );
}